import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Users,
  ShoppingCart,
  DollarSign,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { dashboardApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const Dashboard = () => {
  const { user, isAdmin, isSales } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardApi.getStats();
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Total Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      change: '+5%',
      changeType: 'positive'
    },
    {
      title: 'Pending Orders',
      value: stats?.pending_orders || 0,
      icon: ShoppingCart,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      change: '-3%',
      changeType: 'negative'
    },
    {
      title: 'Total Revenue',
      value: `$${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      change: '+18%',
      changeType: 'positive'
    },
  ];

  const getStatusBadgeClass = (status) => {
    const classes = {
      draft: 'status-draft',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return classes[status] || 'status-draft';
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-md"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="skeleton h-80 rounded-md"></div>
          <div className="skeleton h-80 rounded-md"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Welcome back, {user?.name}
          </h1>
          <p className="text-slate-600 mt-1">
            Here's what's happening with your business today.
          </p>
        </div>
        {(isAdmin || isSales) && (
          <Link to="/portal/sales-orders">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="new-order-button">
              <ShoppingCart className="w-4 h-4 mr-2" />
              New Order
            </Button>
          </Link>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="stats-card" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-md ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div className={`flex items-center text-xs font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.changeType === 'positive' ? (
                    <ArrowUpRight className="w-3 h-3 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 mr-1" />
                  )}
                  {stat.change}
                </div>
              </div>
              <div className="mt-4">
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Low Stock Alert */}
      {stats?.low_stock_count > 0 && (isAdmin || isSales) && (
        <Card className="border-amber-200 bg-amber-50" data-testid="low-stock-alert">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 bg-amber-100 rounded-md">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-800">Low Stock Alert</p>
              <p className="text-sm text-amber-600">
                {stats.low_stock_count} products are running low on inventory
              </p>
            </div>
            <Link to="/portal/inventory">
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                View Inventory
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Recent Orders & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <Card className="lg:col-span-2" data-testid="recent-orders">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Recent Orders</CardTitle>
            <Link to="/portal/sales-orders">
              <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700">
                View All
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_orders && stats.recent_orders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4">Order #</th>
                      <th className="text-left py-3 px-4">Customer</th>
                      <th className="text-right py-3 px-4">Total</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_orders.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 last:border-0">
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm">{order.order_number}</span>
                        </td>
                        <td className="py-3 px-4">{order.customer_name}</td>
                        <td className="py-3 px-4 text-right font-mono">
                          ${order.total.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={`${getStatusBadgeClass(order.status)} capitalize`}>
                            {order.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-500 text-sm">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state py-12">
                <ShoppingCart className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">No recent orders</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-testid="quick-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/portal/products" className="block">
              <Button variant="outline" className="w-full justify-start text-left h-auto py-3">
                <Package className="w-5 h-5 mr-3 text-slate-500" />
                <div>
                  <p className="font-medium">Browse Products</p>
                  <p className="text-xs text-slate-500">View product catalog</p>
                </div>
              </Button>
            </Link>
            
            {(isAdmin || isSales) && (
              <>
                <Link to="/portal/sales-orders" className="block">
                  <Button variant="outline" className="w-full justify-start text-left h-auto py-3">
                    <ShoppingCart className="w-5 h-5 mr-3 text-slate-500" />
                    <div>
                      <p className="font-medium">Create Order</p>
                      <p className="text-xs text-slate-500">New sales order</p>
                    </div>
                  </Button>
                </Link>
                
                <Link to="/portal/customers" className="block">
                  <Button variant="outline" className="w-full justify-start text-left h-auto py-3">
                    <Users className="w-5 h-5 mr-3 text-slate-500" />
                    <div>
                      <p className="font-medium">Manage Customers</p>
                      <p className="text-xs text-slate-500">View customer list</p>
                    </div>
                  </Button>
                </Link>
              </>
            )}

            <Link to="/portal/invoices" className="block">
              <Button variant="outline" className="w-full justify-start text-left h-auto py-3">
                <TrendingUp className="w-5 h-5 mr-3 text-slate-500" />
                <div>
                  <p className="font-medium">View Invoices</p>
                  <p className="text-xs text-slate-500">Check payment status</p>
                </div>
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
